// Live counts of unresolved incidents in the current org. Used by the
// global banner + nav badge so a triggered incident is impossible to
// miss from any page in the app.
//
// The query is `status in ['triggered','acknowledged']` — Firestore
// auto-indexes single-field `in` filters so no composite index is
// required. We do the count + most-recent-triggered pick on the client
// rather than a second `orderBy` query, which would force a custom
// index and cost a second listener for the same docs.

import { useEffect, useState } from 'react';
import { onSnapshot, query, where } from 'firebase/firestore';
import { useOrg } from '../contexts/OrgContext';
import { orgIncidentsCol } from '../lib/firestore';
import { readIncident } from '../lib/incidents';
import type { Incident } from '../lib/types';

export interface OpenIncidentsState {
  /** Incidents in `triggered` state — the alarm signal. */
  triggered: number;
  /** Acknowledged but not resolved — work in progress. */
  acknowledged: number;
  /** Most recent triggered incident, surfaced for the banner deep-link. */
  topTriggered: Incident | null;
  loading: boolean;
}

const EMPTY: OpenIncidentsState = {
  triggered: 0,
  acknowledged: 0,
  topTriggered: null,
  loading: false,
};

export function useOpenIncidents(): OpenIncidentsState {
  const { orgId } = useOrg();
  const [state, setState] = useState<OpenIncidentsState>({ ...EMPTY, loading: true });

  useEffect(() => {
    if (!orgId) {
      setState(EMPTY);
      return;
    }
    const unsub = onSnapshot(
      query(
        orgIncidentsCol(orgId),
        where('status', 'in', ['triggered', 'acknowledged']),
      ),
      (snap) => {
        let triggered = 0;
        let acknowledged = 0;
        let topTriggered: Incident | null = null;
        let topRaisedAt = 0;
        for (const doc of snap.docs) {
          const inc = readIncident(
            doc.data() as Parameters<typeof readIncident>[0],
          );
          if (inc.status === 'triggered') {
            triggered += 1;
            if (inc.raisedAt > topRaisedAt) {
              topRaisedAt = inc.raisedAt;
              topTriggered = inc;
            }
          } else if (inc.status === 'acknowledged') {
            acknowledged += 1;
          }
        }
        setState({ triggered, acknowledged, topTriggered, loading: false });
      },
      (err) => {
        // Permission errors are expected briefly during sign-out — no
        // user-facing surface for this, just degrade quietly.
        console.error('[open-incidents] snapshot failed', err);
        setState(EMPTY);
      },
    );
    return () => unsub();
  }, [orgId]);

  return state;
}
